import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Snackbar, Chip, IconButton,
  CircularProgress, Divider, Accordion, AccordionSummary, AccordionDetails,
  Drawer, Grid, Autocomplete, FormControl, InputLabel, Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactoryIcon from '@mui/icons-material/Factory';
import BusinessIcon from '@mui/icons-material/Business';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { eledgerService } from '../services/eledgerService';
import { nodeNetworkService } from '../services/nodeNetworkService';
import { accountingPeriodService } from '../services/accountingPeriodService';
import {
  NODE_TYPE_COLORS, NODE_ROLE_LABELS, SCOPE_COLORS,
  PACT_STATUS_COLORS, formatEmissions
} from '../constants/nodeTypes';

function ELedgers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedNodeId = searchParams.get('node_id');

  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, ledger: null, entries: [], loading: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dashboard data for node grouping
  const [dashboardData, setDashboardData] = useState(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterSearch, setFilterSearch] = useState('');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [periods, setPeriods] = useState([]);

  // Create dialog state
  const [createForm, setCreateForm] = useState({
    ledger_name: '',
    entity_type: 'facility',
    entity_id: '',
    node_id: '',
    scope: ''
  });
  const [networkNodes, setNetworkNodes] = useState([]);

  useEffect(() => {
    loadPeriods();
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedPeriodId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-select node in accordion if node_id param provided
  useEffect(() => {
    if (preselectedNodeId && dashboardData) {
      // Auto-expand the accordion for the preselected node
      const el = document.getElementById(`accordion-${preselectedNodeId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [preselectedNodeId, dashboardData]);

  const loadPeriods = async () => {
    try {
      const data = await accountingPeriodService.getAll();
      setPeriods(data ?? []);
      // Auto-select the first open period
      const openPeriod = data.find(p => p.status === 'open');
      if (openPeriod) setSelectedPeriodId(openPeriod.period_id);
    } catch (err) {
      // Non-fatal — period selector will be empty
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = { include_node: 'true' };
      if (selectedPeriodId) filters.period_id = selectedPeriodId;
      const [ledgerData, dashData] = await Promise.all([
        eledgerService.getAll(filters),
        nodeNetworkService.getDashboard().catch(() => null)
      ]);
      setLedgers(ledgerData ?? []);
      setDashboardData(dashData);
    } catch (err) {
      setError(err.message || 'Failed to load E-ledgers');
    } finally {
      setLoading(false);
    }
  };

  // Group ledgers by node
  const { groupedByNode, unassignedLedgers } = useMemo(() => {
    const grouped = {};
    const unassigned = [];

    for (const ledger of ledgers) {
      // Apply filters
      if (filterStatus && ledger.status !== filterStatus) continue;
      if (filterSearch && !ledger.ledger_name.toLowerCase().includes(filterSearch.toLowerCase())) continue;

      const nodeId = ledger.node_id || ledger.node?.node_id;
      if (nodeId) {
        if (!grouped[nodeId]) grouped[nodeId] = [];
        grouped[nodeId].push(ledger);
      } else {
        unassigned.push(ledger);
      }
    }
    return { groupedByNode: grouped, unassignedLedgers: unassigned };
  }, [ledgers, filterStatus, filterSearch]);

  // Build node info lookup from dashboard data
  const nodeInfoMap = useMemo(() => {
    const map = {};
    if (dashboardData?.node_health) {
      for (const n of dashboardData.node_health) {
        map[n.node_id] = n;
      }
    }
    // Also enrich from ledger node includes
    for (const l of ledgers) {
      if (l.node && !map[l.node.node_id]) {
        map[l.node.node_id] = {
          node_id: l.node.node_id,
          node_name: l.node.node_name,
          node_type: l.node.node_type,
          node_role: l.node.node_role,
          ofp_organization_id: l.node.ofp_organization_id,
          ofp_facility_id: l.node.ofp_facility_id,
          ofp_boundary_id: l.node.ofp_boundary_id
        };
      }
    }
    return map;
  }, [dashboardData, ledgers]);

  const handleCreateLedger = async () => {
    try {
      const data = {
        ledger_name: createForm.ledger_name,
        entity_type: createForm.entity_type,
        entity_id: createForm.entity_id || crypto.randomUUID(),
        node_id: createForm.node_id || null,
        metadata: createForm.scope ? { scope: createForm.scope } : {}
      };
      await eledgerService.create(data);
      setOpenDialog(false);
      setCreateForm({
        ledger_name: '', entity_type: 'facility', entity_id: '',
        node_id: '', scope: ''
      });
      setSnackbar({ open: true, message: 'E-Ledger created successfully', severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error?.message || 'Failed to create E-ledger', severity: 'error' });
    }
  };

  const handleClosePeriod = async (id) => {
    try {
      const result = await eledgerService.closePeriod(id);
      if (result.warnings && result.warnings.length > 0) {
        setSnackbar({ open: true, message: result.warnings[0], severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: 'Accounting period closed', severity: 'success' });
      }
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error?.message || 'Failed to close period', severity: 'error' });
    }
  };

  const handleViewDetail = async (id) => {
    try {
      setDetailDrawer({ open: true, ledger: null, entries: [], loading: true });
      const [ledger, entries] = await Promise.all([
        eledgerService.getById(id, true),
        eledgerService.getEntries(id).catch(() => [])
      ]);
      setDetailDrawer({ open: true, ledger, entries, loading: false });
    } catch (err) {
      setDetailDrawer({ open: true, ledger: null, entries: [], loading: false });
      setSnackbar({ open: true, message: 'Failed to load ledger details', severity: 'error' });
    }
  };

  const handleOpenCreateDialog = async () => {
    setOpenDialog(true);
    if (networkNodes.length === 0) {
      try {
        const networks = await nodeNetworkService.getNetworks();
        const allNodes = [];
        for (const nw of networks) {
          const nodes = await nodeNetworkService.getNodes(nw.network_id);
          allNodes.push(...nodes.map(n => ({ ...n, network_name: nw.network_name })));
        }
        setNetworkNodes(allNodes);
      } catch (err) {
        // Ignore — node selection is optional
      }
    }
  };

  const getScopeBadge = (metadata) => {
    const scope = metadata?.scope;
    if (!scope) return null;
    return (
      <Chip
        label={scope}
        size="small"
        sx={{
          backgroundColor: SCOPE_COLORS[scope] || SCOPE_COLORS.unscoped,
          color: '#fff',
          fontSize: '0.65rem',
          height: 18
        }}
      />
    );
  };

  const renderNodeHeader = (nodeId) => {
    const info = nodeInfoMap[nodeId];
    if (!info) return <Typography variant="body1">Unknown Node</Typography>;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
          onClick={(e) => { e.stopPropagation(); navigate(`/node-network/nodes/${nodeId}`); }}
        >
          {info.node_name}
        </Typography>
        <Chip
          label={info.node_type}
          size="small"
          sx={{ backgroundColor: NODE_TYPE_COLORS[info.node_type] || '#90a4ae', color: '#fff', fontSize: '0.7rem' }}
        />
        <Chip
          label={NODE_ROLE_LABELS[info.node_role] || info.node_role}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
        {info.bound_identity !== false && info.bound_identity !== undefined && (
          <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Bound" size="small" sx={{ fontSize: '0.65rem', color: '#4caf50' }} />
        )}
        {info.pact_sync_status && (
          <Chip
            label={`PACT: ${info.pact_sync_status}`}
            size="small"
            sx={{
              backgroundColor: PACT_STATUS_COLORS[info.pact_sync_status] || '#9e9e9e',
              color: '#fff',
              fontSize: '0.65rem',
              height: 20
            }}
          />
        )}
        {info.ofp_facility_id ? (
          <Chip
            icon={<FactoryIcon sx={{ fontSize: 14 }} />}
            label={`OFP: ${info.ofp_facility_name || info.ofp_facility_id}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.65rem' }}
          />
        ) : (
          <Chip
            icon={<LinkOffIcon sx={{ fontSize: 14 }} />}
            label="Not in OFP"
            size="small"
            sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {groupedByNode[nodeId]?.length || 0} ledger(s)
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">E-Ledgers</Typography>
          <Typography variant="body2" color="text.secondary">
            Environmental ledgers organized by supply chain node with OFP context
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          Create E-Ledger
        </Button>
      </Box>

      {/* ── Filter Bar ─────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search"
          size="small"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Status"
          select
          size="small"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Accounting Period</InputLabel>
          <Select
            value={selectedPeriodId}
            label="Accounting Period"
            onChange={(e) => setSelectedPeriodId(e.target.value)}
            startAdornment={<CalendarTodayIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />}
          >
            <MenuItem value="">All time (no period)</MenuItem>
            {periods.map(p => (
              <MenuItem key={p.period_id} value={p.period_id}>
                {p.period_name}
                {p.status !== 'open' && (
                  <Chip label={p.status} size="small" sx={{ ml: 1, fontSize: '0.65rem' }}
                    color={p.status === 'closed' ? 'warning' : 'default'} />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedPeriodId && periods.length > 0 && (() => {
          const p = periods.find(x => x.period_id === selectedPeriodId);
          return p ? (
            <Typography variant="caption" color="text.secondary">
              {new Date(p.start_date).toLocaleDateString()} – {new Date(p.end_date).toLocaleDateString()}
            </Typography>
          ) : null;
        })()}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Node Accordion List ────────────────────────────── */}
      {Object.keys(groupedByNode).length === 0 && unassignedLedgers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No E-ledgers found. Click "Create E-Ledger" to get started.</Typography>
        </Paper>
      ) : (
        <>
          {Object.entries(groupedByNode)
            .sort(([aId], [bId]) => {
              const aName = nodeInfoMap[aId]?.node_name || '';
              const bName = nodeInfoMap[bId]?.node_name || '';
              return aName.localeCompare(bName);
            })
            .map(([nodeId, nodeLedgers]) => (
              <Accordion
                key={nodeId}
                id={`accordion-${nodeId}`}
                defaultExpanded={preselectedNodeId === nodeId}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {renderNodeHeader(nodeId)}
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Ledger Name</TableCell>
                          <TableCell>Scope</TableCell>
                          <TableCell align="right">{selectedPeriodId ? 'Period Debits' : 'Debits'}</TableCell>
                          <TableCell align="right">{selectedPeriodId ? 'Period Credits' : 'Credits'}</TableCell>
                          <TableCell align="right">{selectedPeriodId ? 'Period Balance' : 'Balance'}</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nodeLedgers.map((ledger) => {
                            const debit = selectedPeriodId ? (ledger.period_debit_kg_co2e ?? 0) : parseFloat(ledger.total_debit_kg_co2e || 0);
                            const credit = selectedPeriodId ? (ledger.period_credit_kg_co2e ?? 0) : parseFloat(ledger.total_credit_kg_co2e || 0);
                            const balance = selectedPeriodId ? (ledger.period_balance_kg_co2e ?? 0) : parseFloat(ledger.balance_kg_co2e || 0);
                            return (
                          <TableRow key={ledger.e_ledger_id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {ledger.ledger_name}
                              </Typography>
                            </TableCell>
                            <TableCell>{getScopeBadge(ledger.metadata)}</TableCell>
                            <TableCell align="right">{formatEmissions(debit)}</TableCell>
                            <TableCell align="right">{formatEmissions(credit)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ color: balance === 0 ? 'success.main' : 'warning.main' }}
                              >
                                {formatEmissions(balance)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={ledger.status}
                                size="small"
                                color={ledger.status === 'active' ? 'success' : ledger.status === 'closed' ? 'default' : 'error'}
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" title="View Details" onClick={() => handleViewDetail(ledger.e_ledger_id)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {ledger.status === 'active' && (
                                <Button size="small" onClick={() => handleClosePeriod(ledger.e_ledger_id)} sx={{ fontSize: '0.7rem' }}>
                                  Close
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}

          {/* Unassigned ledgers */}
          {unassignedLedgers.length > 0 && (
            <Accordion defaultExpanded={Object.keys(groupedByNode).length === 0} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="subtitle1" fontWeight={600}>Unassigned Ledgers</Typography>
                  <Chip label={unassignedLedgers.length} size="small" color="warning" sx={{ fontSize: '0.7rem' }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ledger Name</TableCell>
                        <TableCell>Entity Type</TableCell>
                        <TableCell align="right">{selectedPeriodId ? 'Period Debits' : 'Debits'}</TableCell>
                        <TableCell align="right">{selectedPeriodId ? 'Period Credits' : 'Credits'}</TableCell>
                        <TableCell align="right">{selectedPeriodId ? 'Period Balance' : 'Balance'}</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unassignedLedgers.map((ledger) => {
                        const debit = selectedPeriodId ? (ledger.period_debit_kg_co2e ?? 0) : parseFloat(ledger.total_debit_kg_co2e || 0);
                        const credit = selectedPeriodId ? (ledger.period_credit_kg_co2e ?? 0) : parseFloat(ledger.total_credit_kg_co2e || 0);
                        const balance = selectedPeriodId ? (ledger.period_balance_kg_co2e ?? 0) : parseFloat(ledger.balance_kg_co2e || 0);
                        return (
                        <TableRow key={ledger.e_ledger_id} hover>
                          <TableCell>{ledger.ledger_name}</TableCell>
                          <TableCell><Chip label={ledger.entity_type} size="small" sx={{ fontSize: '0.7rem' }} /></TableCell>
                          <TableCell align="right">{formatEmissions(debit)}</TableCell>
                          <TableCell align="right">{formatEmissions(credit)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} sx={{ color: balance === 0 ? 'success.main' : 'warning.main' }}>
                              {formatEmissions(balance)}
                            </Typography>
                          </TableCell>
                          <TableCell><Chip label={ledger.status} size="small" color={ledger.status === 'active' ? 'success' : 'default'} sx={{ fontSize: '0.7rem' }} /></TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleViewDetail(ledger.e_ledger_id)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}

      {/* ── Create E-Ledger Dialog ──────────────────────────── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New E-Ledger</DialogTitle>
        <DialogContent>
          <TextField
            label="Ledger Name"
            fullWidth
            margin="normal"
            value={createForm.ledger_name}
            onChange={(e) => setCreateForm({ ...createForm, ledger_name: e.target.value })}
            required
          />
          {networkNodes.length > 0 && (
            <Autocomplete
              options={networkNodes}
              getOptionLabel={(opt) => `${opt.node_name} [${opt.node_type}]`}
              value={networkNodes.find(n => n.node_id === createForm.node_id) || null}
              onChange={(e, val) => {
                setCreateForm({
                  ...createForm,
                  node_id: val?.node_id || '',
                  entity_type: val?.node_type || 'facility',
                  entity_id: val?.ofp_facility_id || val?.node_id || ''
                });
              }}
              renderInput={(params) => <TextField {...params} label="Supply Chain Node" margin="normal" />}
              size="small"
            />
          )}
          {createForm.node_id && networkNodes.find(n => n.node_id === createForm.node_id)?.ofp_facility_id && (
            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
              OFP Facility: {networkNodes.find(n => n.node_id === createForm.node_id)?.ofp_facility_id}
            </Alert>
          )}
          <TextField
            label="Entity Type"
            select
            fullWidth
            margin="normal"
            value={createForm.entity_type}
            onChange={(e) => setCreateForm({ ...createForm, entity_type: e.target.value })}
          >
            <MenuItem value="organization">Organization</MenuItem>
            <MenuItem value="facility">Facility</MenuItem>
            <MenuItem value="equipment">Equipment</MenuItem>
          </TextField>
          <TextField
            label="Entity ID"
            fullWidth
            margin="normal"
            value={createForm.entity_id}
            onChange={(e) => setCreateForm({ ...createForm, entity_id: e.target.value })}
            helperText="Auto-populated from node if selected, or auto-generated if blank"
          />
          <TextField
            label="Scope"
            select
            fullWidth
            margin="normal"
            value={createForm.scope}
            onChange={(e) => setCreateForm({ ...createForm, scope: e.target.value })}
          >
            <MenuItem value="">Unscoped</MenuItem>
            <MenuItem value="S1">Scope 1 — Direct</MenuItem>
            <MenuItem value="S2">Scope 2 — Indirect (Energy)</MenuItem>
            <MenuItem value="S3">Scope 3 — Value Chain</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLedger} disabled={!createForm.ledger_name}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Drawer ───────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={detailDrawer.open}
        onClose={() => setDetailDrawer({ ...detailDrawer, open: false })}
        PaperProps={{ sx: { width: '50%', minWidth: 400, p: 3 } }}
      >
        {detailDrawer.loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : detailDrawer.ledger ? (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">{detailDrawer.ledger.ledger_name}</Typography>
              <IconButton onClick={() => setDetailDrawer({ ...detailDrawer, open: false })}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Chip
                label={detailDrawer.ledger.status}
                size="small"
                color={detailDrawer.ledger.status === 'active' ? 'success' : detailDrawer.ledger.status === 'closed' ? 'default' : 'error'}
              />
              {getScopeBadge(detailDrawer.ledger.metadata)}
              <Chip label={detailDrawer.ledger.entity_type} size="small" variant="outlined" />
            </Box>

            {/* Node context */}
            {detailDrawer.ledger.node && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary">Supply Chain Node</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                    onClick={() => navigate(`/node-network/nodes/${detailDrawer.ledger.node.node_id}`)}
                  >
                    {detailDrawer.ledger.node.node_name}
                  </Typography>
                  <Chip
                    label={detailDrawer.ledger.node.node_type}
                    size="small"
                    sx={{ backgroundColor: NODE_TYPE_COLORS[detailDrawer.ledger.node.node_type] || '#90a4ae', color: '#fff', fontSize: '0.65rem' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/node-network/nodes/${detailDrawer.ledger.node.node_id}`)}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Paper>
            )}

            {/* OFP context */}
            {detailDrawer.ledger.node?.ofp_facility_id && (() => {
              const node = detailDrawer.ledger.node;
              const info = nodeInfoMap[node.node_id];
              return (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderLeft: '3px solid #00796b' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Open Footprint Context</Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <BusinessIcon sx={{ fontSize: 16, color: '#00796b' }} />
                    <Typography variant="body2">{info?.ofp_organization_name || node.ofp_organization_id}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <FactoryIcon sx={{ fontSize: 16, color: '#00796b' }} />
                    <Typography variant="body2">{info?.ofp_facility_name || node.ofp_facility_id}</Typography>
                  </Box>
                  {info?.ofp_boundary_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                      Boundary: {info.ofp_boundary_name}
                    </Typography>
                  )}
                  {info?.ofp_emission_activity_count > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Chip label={`${info.ofp_emission_activity_count} activities`} size="small" sx={{ fontSize: '0.65rem' }} />
                      <Chip label={`${info.ofp_emission_statement_count} statements`} size="small" sx={{ fontSize: '0.65rem' }} />
                    </Box>
                  )}
                </Paper>
              );
            })()}

            {/* Metrics grid */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Entity ID</Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" noWrap>{detailDrawer.ledger.entity_id}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Debits</Typography>
                <Typography variant="body2">{formatEmissions(parseFloat(detailDrawer.ledger.total_debit_kg_co2e || 0))}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Credits</Typography>
                <Typography variant="body2">{formatEmissions(parseFloat(detailDrawer.ledger.total_credit_kg_co2e || 0))}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Balance</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: parseFloat(detailDrawer.ledger.balance_kg_co2e) === 0 ? 'success.main' : 'warning.main' }}>
                  {formatEmissions(parseFloat(detailDrawer.ledger.balance_kg_co2e || 0))}
                </Typography>
              </Grid>
            </Grid>

            {/* Actions */}
            <Box display="flex" gap={1} mb={2}>
              {detailDrawer.ledger.status === 'active' && (
                <Button size="small" variant="outlined" onClick={() => handleClosePeriod(detailDrawer.ledger.e_ledger_id)}>
                  Close Period
                </Button>
              )}
              <Button size="small" variant="outlined" onClick={() => {
                eledgerService.validate(detailDrawer.ledger.e_ledger_id).then(result => {
                  setSnackbar({
                    open: true,
                    message: result.is_valid ? 'Ledger integrity verified' : 'Ledger integrity check failed',
                    severity: result.is_valid ? 'success' : 'error'
                  });
                });
              }}>
                Validate
              </Button>
            </Box>

            {/* Metadata */}
            {detailDrawer.ledger.metadata && Object.keys(detailDrawer.ledger.metadata).length > 0 && (
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">Metadata</Typography>
                <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                    {JSON.stringify(detailDrawer.ledger.metadata, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Entries table */}
            <Typography variant="subtitle2" gutterBottom>
              Ledger Entries ({detailDrawer.entries.length})
            </Typography>
            {detailDrawer.entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No entries recorded for this ledger.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount (kg CO2e)</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailDrawer.entries.map((entry) => (
                    <TableRow key={entry.entry_id || entry.e_ledger_entry_id}>
                      <TableCell>{entry.entry_date || entry.created_at?.split('T')[0] || '—'}</TableCell>
                      <TableCell><Chip label={entry.entry_type || '—'} size="small" sx={{ fontSize: '0.7rem' }} /></TableCell>
                      <TableCell>{entry.description || '—'}</TableCell>
                      <TableCell align="right">{parseFloat(entry.amount_kg_co2e || 0).toLocaleString()}</TableCell>
                      <TableCell>{entry.source_type || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : (
          <Typography color="text.secondary">Ledger not found.</Typography>
        )}
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ELedgers;
